import React, { useState, useEffect, useMemo } from "react"
import PropTypes from "prop-types"
import { Modal, Button, Table, Typography, Flex, Tag, Form } from "antd"
import { DeleteOutlined, CheckOutlined } from "@ant-design/icons"
import { useTranslation } from "react-i18next"
import { useDispatch, useSelector } from "react-redux"
import ExperienceSelector from "pagesV2/pro/Equipment/experience/ExperienceSelector"
import LabeledField from "pagesV2/client/Profile/Tabs/ProfileInfo/LabeledField"
import { Text } from "ui/Typography"
import { spacings } from "themes/variables"
import { getTrips, updateBoat, getEquipmentDetails } from "store/boats"
import { toaster } from "components/common/Toast"
import styles from "./index.module.css"

const { Title } = Typography

const AssignExperiencesModal = ({ open, onCancel, equipmentId, currentExperiences = [] }) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const [form] = Form.useForm()
  const [selectedExperiences, setSelectedExperiences] = useState([])
  const [isSubmitting, setIsSubmitting] = useState(false)

  // Get experiences from Redux store
  const tripsState = useSelector((state) => state?.trips?.items || [])

  // Memoized experiences list
  const experiencesMemo = useMemo(
    () =>
      tripsState.map((trip) => ({
        id: String(trip.id),
        name: trip.title || "Untitled",
        guests: trip.max_guests || 0,
        price: trip.price_per_person ? Number(trip.price_per_person) : 0,
        status: trip.status,
        currency: "$"
      })),
    [tripsState]
  )

  // Derive selected experience data for table
  const selectedExperienceData = useMemo(
    () => (selectedExperiences.length ? experiencesMemo.filter((exp) => selectedExperiences.includes(String(exp.id))) : []),
    [selectedExperiences, experiencesMemo]
  )

  // Fetch experiences on mount
  useEffect(() => {
    dispatch(getTrips())
  }, [dispatch])

  // Initialize selected experiences from current equipment experiences
  useEffect(() => {
    if (open && currentExperiences) {
      const experienceIds = currentExperiences.map((exp) => String(exp.id))
      setSelectedExperiences(experienceIds)
      form.setFieldsValue({ experiences: experienceIds })
    }
  }, [open, currentExperiences, form])

  const handleRemoveExperience = (expId) => {
    setSelectedExperiences((prev) => {
      const next = prev.filter((id) => id !== expId)
      form.setFieldsValue({ experiences: next })
      return next
    })
  }

  const handleSubmit = async (values) => {
    if (!equipmentId) {
      toaster.error(t("equipment.errors.no_equipment_selected"))
      return
    }

    setIsSubmitting(true)

    // Build FormData with only trip_ids
    const formData = new FormData()
    for (const tripId of selectedExperiences) {
      formData.append("boat[trip_ids][]", tripId)
    }

    // If no experiences selected, send empty array indicator
    if (selectedExperiences.length === 0) {
      formData.append("boat[trip_ids][]", "")
    }

    try {
      await dispatch(updateBoat(equipmentId, formData))
      toaster.success(t("equipment.experiences_assigned_successfully", { defaultValue: "Experiences assigned successfully" }))
      // Refresh equipment details to update the list
      await dispatch(getEquipmentDetails(equipmentId))
      onCancel()
    } catch (error) {
      toaster.error(error?.message || t("equipment.experiences_assign_failed", { defaultValue: "Failed to assign experiences" }))
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleCancel = () => {
    form.resetFields()
    setSelectedExperiences([])
    onCancel()
  }

  const columns = [
    {
      title: t("equipment.details.columns.experience_name"),
      dataIndex: "name",
      key: "name",
      render: (name) => <Text fw="500">{name}</Text>
    },
    {
      title: t("equipment.details.columns.guests"),
      dataIndex: "guests",
      key: "guests",
      align: "center",
      render: (guests) => <Text>{guests}</Text>
    },
    {
      title: t("equipment.details.columns.price"),
      key: "price",
      align: "right",
      render: (_, record) => (
        <Text fw="400">
          {record.currency}
          {record.price.toFixed(2)}
        </Text>
      )
    },
    {
      title: t("equipment.details.columns.status"),
      dataIndex: "status",
      key: "status",
      align: "left",
      render: (status) => {
        const isPublished = String(status).toLowerCase() === "published"
        const displayText = String(status).charAt(0).toUpperCase() + String(status).slice(1)
        return (
          <Tag color={isPublished ? "green" : "default"} icon={isPublished ? <CheckOutlined /> : null}>
            {displayText}
          </Tag>
        )
      }
    },
    {
      title: "",
      key: "action",
      align: "right",
      render: (_, record) => (
        <Button
          type="text"
          icon={<DeleteOutlined className={styles.deleteIcon} />}
          onClick={() => handleRemoveExperience(record.id)}
          className={styles.removeButton}
          aria-label="Remove experience"
        />
      )
    }
  ]

  return (
    <Modal
      title={
        <Title level={4}>
          {t("equipment.modals.assign_experiences")} <span className={styles.experiencesCount}>({selectedExperienceData.length})</span>
        </Title>
      }
      open={open}
      onCancel={handleCancel}
      className={styles.assignExperiencesModal}
      width={800}
      footer={null}
      maskClosable={false}
    >
      <Form form={form} onFinish={handleSubmit} layout="vertical">
        <Flex vertical gap={spacings.M}>
          <LabeledField
            name="experiences"
            labelKey={t("equipment.experiences")}
            rules={[{ required: true, message: t("equipment.experience_required") }]}
          >
            <ExperienceSelector
              experiences={experiencesMemo}
              value={selectedExperiences}
              onChange={(vals) => {
                const strVals = vals.map(String)
                setSelectedExperiences(strVals)
                form.setFieldsValue({ experiences: strVals })
              }}
            />
          </LabeledField>

          {selectedExperienceData.length > 0 && (
            <div>
              <Title level={5} className={styles.assignedTitle}>
                {t("equipment.assigned_to_experiences", { defaultValue: "Assigned Experiences" })}
              </Title>
              <div className={styles.tableWrapper}>
                <Table dataSource={selectedExperienceData} rowKey="id" pagination={false} columns={columns} className={styles.experiencesTable} />
              </div>
            </div>
          )}

          {selectedExperienceData.length === 0 && (
            <Text type="secondary" className={styles.emptyText}>
              {t("equipment.no_experience_assigned", { defaultValue: "No experience assigned to this equipment." })}
            </Text>
          )}

          <Flex gap={spacings.S} justify="flex-end" className={styles.footerActions}>
            <Button onClick={handleCancel} className={styles.cancelBtn}>
              {t("common.cancel")}
            </Button>
            <Button type="primary" htmlType="submit" loading={isSubmitting} className={styles.saveBtn}>
              {t("common.save")}
            </Button>
          </Flex>
        </Flex>
      </Form>
    </Modal>
  )
}

AssignExperiencesModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onCancel: PropTypes.func.isRequired,
  equipmentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  currentExperiences: PropTypes.array
}

export default AssignExperiencesModal
